import { useState } from 'react';
import { PROJECTS, CASES } from './data.js';
import PrototypeModal from './PrototypeModal.jsx';
import { onNavClick } from './router.js';

export default function CaseStudy({ slug }) {
  const data = CASES[slug];
  const proj = PROJECTS.find(p => p.id === slug);

  if (!data) return <CaseStudyOnRequest proj={proj} />;

  return (
    <article className="cs">
      <CSHero data={data} />
      {data.subProjects && <CSSubProjects data={data.subProjects} caseTitle={proj?.title || data.title} />}
      {data.problem   && <CSSection n="01" label="Problem"    title="The problem"                body={data.problem} />}
      {data.research  && <CSSection n="02" label="Research"   title="Research"                   body={data.research} />}
      {data.framing   && <CSSection n="03" label="Framing"    title="How we framed it"           body={data.framing} />}
      {data.process   && <CSSection n="04" label="Process"    title="Process"                    body={data.process} />}
      {data.decisions && <CSDecisions data={data.decisions} />}
      {data.artifacts && <CSArtifacts data={data.artifacts} />}
      {data.outcomes  && <CSOutcomes  data={data.outcomes} />}
      {data.reflection && <CSSection n="09" label="Reflection" title="What I'd do differently"   body={data.reflection} />}
      {data.next && <CSNext next={data.next} />}
    </article>
  );
}

function CSSubProjects({ data, caseTitle }) {
  const [proto, setProto] = useState(null);
  const isInternal = (href) => typeof href === 'string' && href.startsWith('/') && !href.endsWith('.pdf');
  return (
    <section className="cs-section cs-subprojects">
      <div className="container">
        <div className="cs-subprojects__list">
          {data.map((sp, i) => (
            <article key={i} className="cs-subproject">
              <div className="cs-subproject__num">{String(i + 1).padStart(2, '0')}</div>
              {sp.thumb && (
                <div className="cs-subproject__thumb" style={sp.thumbBg ? { background: sp.thumbBg } : undefined}>
                  <img src={sp.thumb} alt="" loading="lazy" style={sp.thumbFit ? { objectFit: sp.thumbFit } : undefined} />
                </div>
              )}
              <div className="cs-subproject__body">
                <h3 className="cs-subproject__title">{sp.title}</h3>
                <p className="cs-subproject__desc">{sp.description}</p>
                {sp.cta && (
                  isInternal(sp.cta.href) ? (
                    <button
                      type="button"
                      className="cs-subproject__cta"
                      onClick={() => setProto({ url: sp.cta.href, label: sp.title })}
                    >
                      {sp.cta.label} <span>→</span>
                    </button>
                  ) : (
                    <a href={sp.cta.href} target="_blank" rel="noopener noreferrer" className="cs-subproject__cta">
                      {sp.cta.label} <span>→</span>
                    </a>
                  )
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
      {proto && <PrototypeModal url={proto.url} label={proto.label} caseTitle={caseTitle} onClose={() => setProto(null)} />}
    </section>
  );
}

function CSHero({ data }) {
  return (
    <section className="cs-hero">
      <div className="container">
        <div className="cs-breadcrumb">
          <a href="/" onClick={onNavClick}>← All work</a>
          <span>/</span>
          <span>{data.overview && data.overview.scope ? data.overview.scope.split('·')[0].trim() : 'Case study'}</span>
        </div>
        <h1 className="cs-title">{data.title}</h1>
        {data.subtitle && <p className="cs-subtitle">{data.subtitle}</p>}
      </div>
      <div className="container">
        <div className="cs-cover" style={data.tile ? { background: data.tile.bg } : null}>
          {data.tile?.iframe ? (
            <iframe
              src={data.tile.iframe}
              title={`${data.title} live preview`}
              className="cs-cover__iframe"
              loading="lazy"
            />
          ) : data.tile?.image ? (
            <img src={data.tile.image} alt="" className="cs-cover__img" />
          ) : (
            <>
              <div className="cs-cover__stripe" />
              <div className="cs-cover__label">Fig. 01 · Case opener</div>
              <div className="cs-cover__mark">{data.title.split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase()}</div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function CSSection({ n, label, title, body }) {
  const paragraphs = Array.isArray(body) ? body : [body];
  return (
    <section className="cs-section">
      <div className="container">
        <div className="cs-section__grid">
          <div className="cs-section__label">
            <span className="cs-section__num">{n} · {label}</span>
          </div>
          <div className="cs-section__body">
            <h2 className="cs-section__title">{title}</h2>
            {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>
      </div>
    </section>
  );
}

function CSDecisions({ data }) {
  return (
    <section className="cs-section">
      <div className="container">
        <div className="cs-section__grid">
          <div className="cs-section__label">
            <span className="cs-section__num">05 · Decisions</span>
          </div>
          <div className="cs-section__body">
            <h2 className="cs-section__title">Key design decisions</h2>
            <ol className="cs-decisions">
              {data.map((d, i) => (
                <li key={i} className="cs-decision">
                  <span className="cs-decision__num">0{i + 1}</span>
                  <h3 className="cs-decision__h">{d.h}</h3>
                  <p className="cs-decision__b">{d.b}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

function CSArtifacts({ data }) {
  return (
    <section className="cs-section cs-artifacts">
      <div className="container">
        <header className="sec-head">
          <span className="sec-head__num">06 · Artifacts</span>
          <h2 className="sec-head__title">Final designs</h2>
          <span className="sec-head__meta">{data.length} selected</span>
        </header>
        <div className="cs-artifacts__grid">
          {data.map((c, i) => (
            <div key={i} className={"cs-artifact" + (c.dark ? ' cs-artifact--dark' : '')} style={{ background: c.bg }}>
              <div className="cs-artifact__title">{c.title}</div>
              <div className="cs-artifact__label">
                <span>{c.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CSOutcomes({ data }) {
  return (
    <section className="cs-section cs-metrics">
      <div className="container">
        <header className="sec-head">
          <span className="sec-head__num">07 · Outcomes</span>
          <h2 className="sec-head__title">What shipped</h2>
          <span className="sec-head__meta">{data.length} metrics</span>
        </header>
        <div className="cs-metrics__grid">
          {data.map((m, i) => (
            <div key={i} className="cs-metric">
              <div className="cs-metric__label">{m.label}</div>
              <div className="cs-metric__num">{m.num}</div>
              <div className="cs-metric__sub">{m.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CSNext({ next }) {
  return (
    <section className="cs-next">
      <div className="container">
        <div className="cs-next__label">Next case</div>
        <a href={`/case/${next.slug}`} onClick={onNavClick} className="cs-next__link">
          {next.title}<span className="cs-next__arrow">→</span>
        </a>
      </div>
    </section>
  );
}

function CaseStudyOnRequest({ proj }) {
  if (!proj) {
    return (
      <section className="cs-hero"><div className="container">
        <div className="cs-breadcrumb"><a href="/" onClick={onNavClick}>← All work</a></div>
        <h1 className="cs-title">Not found</h1>
        <p className="cs-subtitle">This case study doesn't exist. <a href="#/">Back to work →</a></p>
      </div></section>
    );
  }
  return (
    <section className="cs-hero cs-onrequest">
      <div className="container">
        <div className="cs-breadcrumb">
          <a href="/" onClick={onNavClick}>← All work</a>
          <span>/</span>
          <span>{proj.tag}</span>
        </div>
        <h1 className="cs-title">{proj.title}</h1>
        <p className="cs-subtitle">{proj.summary}</p>
      </div>
      <div className="container">
        <div className="cs-onrequest__card">
          <div className="cs-onrequest__art" style={{ background: proj.tile.bg }} />
          <div className="cs-onrequest__body">
            <h2>Full case study available on request.</h2>
            <p>This engagement contains work under NDA. I'm happy to walk through it in detail in an interview conversation, or to share a confidential read with an appropriate confidentiality agreement in place.</p>
            <dl className="cs-onrequest__facts">
              <div><dt>Role</dt><dd>{proj.role}</dd></div>
              <div><dt>Sector</dt><dd>{proj.tag}</dd></div>
              <div><dt>Outcome</dt><dd>{proj.outcome}</dd></div>
            </dl>
            <a href={`mailto:ryanmarfone@gmail.com?subject=Request full case study: ${proj.title}`} className="cs-onrequest__cta">
              Request full case <span>→</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
